import { ReactElement } from 'react';
import { createUnifiedTheme, UnifiedThemeProvider } from '@backstage/theme';
import { colors, fontFamilyCorpoSText } from '../tokens';

export const odpV2DarkTheme = createUnifiedTheme({
  palette: {
    mode: 'dark',
    background: {
      default: colors.background.page,
      paper: colors.background.paper,
    },
    primary: {
      main: colors.primary.main,
    },
    secondary: {
      main: colors.primary.main,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    divider: colors.border.default,
    status: {
      ok: '#71CF88',
      warning: '#FFB84D',
      error: '#F84C55',
      running: '#3488E3',
      pending: '#FEF071',
      aborted: '#9E9E9E',
    },
    error: {
      main: colors.status.critical,
    },
    warning: {
      main: colors.status.degraded,
    },
    success: {
      main: colors.status.healthy,
    },
    // Backstage palette extensions required by @backstage/theme
    textVerySubtle: '#727272',
    textSubtle: '#CCCCCC',
    textContrast: '#FFFFFF',
    highlight: '#000',
    errorBackground: '#FFEBEE',
    warningBackground: '#F59B23',
    infoBackground: '#303030',
    errorText: '#FF6B6B',
    infoText: '#9CC9FF',
    warningText: '#FFFFFF',
    linkHover: '#82BAFD',
    link: '#9CC9FF',
    gold: '#FFD600',
    banner: {
      info: '#2E77D0',
      error: '#E22134',
      text: '#FFFFFF',
      link: '#000000',
      closeButtonColor: '#FFFFFF',
      warning: '#FF9800',
    },
    navigation: {
      background: '#1A1A1A',
      indicator: colors.primary.main,
      color: '#b5b5b5',
      selectedColor: '#FFFFFF',
      navItem: {
        hoverBackground: 'rgba(255, 255, 255, 0.07)',
      },
    },
  } as any,
  fontFamily: fontFamilyCorpoSText,
  typography: {
    fontFamily: fontFamilyCorpoSText,
    h1: { fontFamily: fontFamilyCorpoSText },
    h2: { fontFamily: fontFamilyCorpoSText },
    h3: { fontFamily: fontFamilyCorpoSText },
    h4: { fontFamily: fontFamilyCorpoSText },
    h5: { fontFamily: fontFamilyCorpoSText },
    h6: { fontFamily: fontFamilyCorpoSText },
    subtitle1: { fontFamily: fontFamilyCorpoSText },
    subtitle2: { fontFamily: fontFamilyCorpoSText },
    body1: { fontFamily: fontFamilyCorpoSText },
    body2: { fontFamily: fontFamilyCorpoSText },
    button: { fontFamily: fontFamilyCorpoSText },
    caption: { fontFamily: fontFamilyCorpoSText },
    overline: { fontFamily: fontFamilyCorpoSText },
  } as any,
  components: {
    // --- MUI core overrides ---
    MuiTypography: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A1A',
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.border.default}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1A1A1A',
          borderRight: `1px solid ${colors.border.default}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.background.paper,
          borderColor: colors.border.default,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          backgroundImage: 'none',
          border: `1px solid ${colors.border.default}`,
          borderRadius: '12px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: colors.primary.main,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#006ABD',
          },
        },
        outlinedPrimary: {
          borderColor: colors.primary.main,
          color: colors.primary.main,
          '&:hover': {
            borderColor: '#4A9EFF',
            backgroundColor: 'rgba(0, 120, 214, 0.08)',
          },
        },
        text: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.07)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.border.default}`,
        },
        indicator: {
          backgroundColor: colors.primary.main,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          '&.Mui-selected': {
            color: colors.text.primary,
          },
          '&:hover': {
            color: 'rgba(255, 255, 255, 0.9)',
            backgroundColor: 'rgba(255, 255, 255, 0.07)',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          borderRadius: '12px',
          border: `1px solid ${colors.border.default}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: colors.background.elevated,
            color: colors.text.secondary,
            fontWeight: 600,
            borderBottom: `1px solid ${colors.border.default}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          borderBottom: `1px solid ${colors.border.default}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
        },
        selectIcon: {
          color: colors.text.secondary,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          color: colors.text.primary,
          borderRadius: '8px',
        },
        input: {
          color: colors.text.primary,
          '&::placeholder': {
            color: colors.text.secondary,
            opacity: 1,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: colors.text.primary,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          '&.Mui-focused': {
            color: colors.primary.main,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.default,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
        },
        notchedOutline: {
          borderColor: colors.border.default,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: colors.text.secondary,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.default}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.07)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 120, 214, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(0, 120, 214, 0.25)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.elevated,
          color: colors.text.primary,
          borderColor: colors.border.default,
        },
        outlined: {
          borderColor: colors.border.default,
        },
        colorPrimary: {
          backgroundColor: colors.primary.main,
          color: '#ffffff',
          borderColor: colors.primary.main,
        },
        filledPrimary: {
          backgroundColor: colors.primary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: colors.primary.hover,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          backgroundImage: 'none',
          border: `1px solid ${colors.border.default}`,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          backgroundImage: 'none',
          borderRadius: '8px',
          border: `1px solid ${colors.border.default}`,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.background.elevated,
          color: colors.text.primary,
          border: `1px solid ${colors.border.default}`,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: colors.border.default,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: colors.border.default,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.border.default,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: colors.text.secondary,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#4A9EFF',
        },
      },
    },
    // --- Backstage component overrides ---
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: 'none',
          color: colors.text.primary,
        },
        title: {
          color: colors.text.primary,
        },
        subtitle: {
          color: colors.text.secondary,
        },
      },
    },
    BackstageTable: {
      styleOverrides: {
        root: {
          '& .MuiToolbar-root': {
            backgroundColor: colors.background.paper,
            color: colors.text.primary,
          },
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: 'transparent',
        },
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.default,
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.default,
        },
      },
    },
    BackstageFavoriteToggleIcon: {
      styleOverrides: {
        icon: { display: 'none' },
        iconBorder: { display: 'none' },
      },
    },
  },
});

/**
 * Theme provider config object for Backstage's createApp `themes` array.
 * Usage in App.tsx:
 *   import { odpV2DarkThemeProvider } from '@mercedes-benz/odp-theme-v2';
 *   themes: [odpV2DarkThemeProvider, ...]
 */
const Provider = ({ children }: { children: ReactElement }) => (
  <UnifiedThemeProvider theme={odpV2DarkTheme}>{children}</UnifiedThemeProvider>
);

export const odpV2DarkThemeProvider = {
  id: 'odp-v2-dark',
  title: 'ODP Dark (New)',
  variant: 'dark' as const,
  Provider,
};
